import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Box, Chip, Tooltip, Typography } from "@mui/material"
import { useState } from "react"

import {
  LoopLine,
  LoopReply,
  LoopRound,
  LoopToolCall,
  V2AgentLoop as V2AgentLoopData
} from "./v2AgentLoop.utils"
import { ACTION_LABELS, PHASE_HINTS } from "./v2AgentLoop.labels"

const MONO =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace"

const seconds = (ms: number) => `${(ms / 1000).toFixed(2)}s`

const tokens = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`)

const FAILED_FG = "#b71c1c"
const FAILED_BG = "rgba(211, 47, 47, 0.06)"
const EXPECTED_FG = "#a15c00"
const EXPECTED_BG = "rgba(237, 108, 2, 0.07)"

export const ACTION_TONE: Record<string, string> = {
  answer: "#1b5e20",
  clarify: "#795548",
  handoff: "#e65100",
  spam: "#b71c1c",
  no_reply: "#455a64"
}

function toolFamily(tool: string): string {
  if (tool === "search_knowledge") return "Bilgi"
  if (
    tool.startsWith("skill_") ||
    tool === "start_skill" ||
    tool === "find_skill"
  ) {
    return "Skill"
  }
  if (tool.startsWith("action_")) return "Action"
  return "Mağaza"
}

const FAMILY_TONE: Record<string, { fg: string; bg: string; border: string }> =
  {
    Bilgi: {
      fg: "#866b2b",
      bg: "rgba(160, 126, 44, 0.07)",
      border: "#c8b77e"
    },
    Skill: {
      fg: "#76528a",
      bg: "rgba(118, 82, 138, 0.07)",
      border: "#b9a4c3"
    },
    Action: {
      fg: "#9a5d35",
      bg: "rgba(154, 93, 53, 0.07)",
      border: "#cba38a"
    },
    Mağaza: {
      fg: "#286477",
      bg: "rgba(40, 100, 119, 0.07)",
      border: "#78a9b5"
    }
  }

type Jump = (stepId: string) => void

function Clickable({
  stepId,
  onJump,
  children,
  sx
}: {
  stepId: string
  onJump?: Jump
  children: React.ReactNode
  sx?: object
}) {
  return (
    <Box
      role={onJump ? "button" : undefined}
      tabIndex={onJump ? 0 : undefined}
      title={onJump ? "Ham adıma git" : undefined}
      onClick={onJump ? () => onJump(stepId) : undefined}
      onKeyDown={
        onJump
          ? (e) => {
              if (e.key === "Enter") onJump(stepId)
            }
          : undefined
      }
      sx={{
        borderRadius: 1,
        ...(onJump && {
          cursor: "pointer",
          "&:hover": { outline: "1px solid rgba(0,0,0,0.12)" }
        }),
        ...sx
      }}
    >
      {children}
    </Box>
  )
}

function Duration({ ms }: { ms: number }) {
  if (!ms) return null
  return (
    <Typography
      variant="caption"
      sx={{
        fontFamily: MONO,
        color: "text.secondary",
        flexShrink: 0,
        ml: "auto",
        pl: 1
      }}
    >
      {seconds(ms)}
    </Typography>
  )
}

function PhaseLabel({
  label,
  hint,
  ms,
  open,
  onToggle,
  collapsedText
}: {
  label: string
  hint: string
  ms?: number
  open?: boolean
  onToggle?: () => void
  collapsedText?: string
}) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        mt: 1.25,
        mb: 0.25,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        minWidth: 0,
        ...(onToggle && {
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" }
        })
      }}
    >
      <Tooltip arrow title={hint} placement="top-start">
        <Typography
          variant="caption"
          sx={{
            flexShrink: 0,
            textTransform: "uppercase",
            fontSize: 10,
            letterSpacing: 0.6,
            fontWeight: 700,
            color: "text.disabled"
          }}
        >
          {label}
        </Typography>
      </Tooltip>
      {onToggle && !open && collapsedText ? (
        <Typography
          variant="caption"
          noWrap
          sx={{ color: "text.secondary", minWidth: 0 }}
        >
          {collapsedText}
        </Typography>
      ) : null}
      {ms ? <Duration ms={ms} /> : null}
      {onToggle ? (
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            flexShrink: 0,
            ml: ms ? 0 : "auto",
            color: "text.disabled",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s"
          }}
        />
      ) : null}
    </Box>
  )
}

function HostLines({ lines, onJump }: { lines: LoopLine[]; onJump?: Jump }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {lines.map((line) => (
        <Clickable key={line.stepId} stepId={line.stepId} onJump={onJump}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.75,
              px: 0.5,
              py: 0.125,
              minWidth: 0
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                flexShrink: 0,
                minWidth: 96,
                color: line.failed ? FAILED_FG : "text.primary"
              }}
            >
              {line.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: line.failed ? FAILED_FG : "text.secondary",
                minWidth: 0,
                overflowWrap: "anywhere"
              }}
            >
              {line.detail}
            </Typography>
            <Duration ms={line.durationMs} />
          </Box>
        </Clickable>
      ))}
    </Box>
  )
}

function ToolCallRow({ call, onJump }: { call: LoopToolCall; onJump?: Jump }) {
  const tone = FAMILY_TONE[toolFamily(call.tool)]
  const accent = call.expected
    ? EXPECTED_FG
    : call.failed
      ? FAILED_FG
      : tone.border
  const background = call.expected
    ? EXPECTED_BG
    : call.failed
      ? FAILED_BG
      : tone.bg
  const resultColor = call.expected
    ? EXPECTED_FG
    : call.failed
      ? FAILED_FG
      : "text.primary"
  return (
    <Clickable
      stepId={call.stepId}
      onJump={onJump}
      sx={{
        mt: 0.5,
        px: 1,
        py: 0.625,
        borderLeft: `3px solid ${accent}`,
        bgcolor: background,
        minWidth: 0
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", flexShrink: 0 }}
        >
          →
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: call.failed && !call.expected ? FAILED_FG : tone.fg,
            overflowWrap: "anywhere"
          }}
        >
          {call.label}
        </Typography>
        {call.label !== call.tool ? (
          <Typography
            variant="caption"
            sx={{
              fontFamily: MONO,
              fontSize: 10,
              color: "text.disabled",
              overflowWrap: "anywhere"
            }}
          >
            {call.tool}
          </Typography>
        ) : null}
        <Duration ms={call.durationMs} />
      </Box>

      {call.intents.map((intent) => (
        <Typography
          key={`${intent.label}-${intent.text}`}
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            overflowWrap: "anywhere"
          }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            {intent.label}:
          </Box>{" "}
          <Box component="span" sx={{ fontStyle: "italic" }}>
            “{intent.text}”
          </Box>
        </Typography>
      ))}

      {call.args.length > 0 && (
        <Box sx={{ mt: 0.25, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {call.args.map((arg) => (
            <Chip
              key={arg}
              size="small"
              variant="outlined"
              label={arg}
              sx={{
                height: 18,
                fontSize: 10.5,
                fontFamily: MONO,
                maxWidth: "100%"
              }}
            />
          ))}
        </Box>
      )}

      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 0.25,
          fontWeight: 600,
          color: resultColor,
          overflowWrap: "anywhere"
        }}
      >
        {call.expected ? "⏸" : call.failed ? "✕" : "✓"} Sonuç: {call.result}
      </Typography>
      {call.resultLines.map((line) => (
        <Typography
          key={line}
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            pl: 1.5,
            overflowWrap: "anywhere"
          }}
        >
          {line}
        </Typography>
      ))}

      {call.inner.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.25,
            fontSize: 10.5,
            color: "text.disabled",
            overflowWrap: "anywhere"
          }}
        >
          arka planda:{" "}
          {call.inner
            .map(
              (line) =>
                `${line.label}${line.failed ? " ✕" : ""}${
                  line.durationMs ? ` ${seconds(line.durationMs)}` : ""
                }`
            )
            .join(" · ")}
        </Typography>
      )}
    </Clickable>
  )
}

function ReplyBubble({
  reply,
  meta,
  onJump
}: {
  reply: LoopReply
  meta: string[]
  onJump?: Jump
}) {
  const tone = ACTION_TONE[reply.action] ?? "#455a64"
  const [full, setFull] = useState(false)
  const long = reply.reply.length > 220
  return (
    <Clickable
      stepId={reply.stepId}
      onJump={onJump}
      sx={{
        mt: 0.5,
        px: 1,
        py: 0.75,
        borderLeft: `3px solid ${reply.failed ? FAILED_FG : tone}`,
        bgcolor: reply.failed ? FAILED_BG : "#e5f0fd"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, flexShrink: 0 }}>
          Ajanın kararı
        </Typography>
        <Chip
          size="small"
          label={
            reply.failed
              ? "Ajan çöktü"
              : (ACTION_LABELS[reply.action] ?? reply.action)
          }
          sx={{
            height: 18,
            fontSize: 10.5,
            fontWeight: 700,
            color: reply.failed ? FAILED_FG : tone,
            bgcolor: "rgba(255,255,255,0.7)"
          }}
        />
        {meta.length > 0 ? (
          <Typography
            variant="caption"
            noWrap
            sx={{ color: "text.secondary", fontSize: 10.5, minWidth: 0 }}
          >
            {meta.join(" · ")}
          </Typography>
        ) : null}
      </Box>
      {reply.failed && reply.error ? (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.5, color: FAILED_FG }}
        >
          {reply.error}
        </Typography>
      ) : null}
      {reply.reply ? (
        <Typography
          variant="body2"
          title={long && !full ? "Tamamını göster" : undefined}
          onClick={
            long
              ? (e) => {
                  e.stopPropagation()
                  setFull((prev) => !prev)
                }
              : undefined
          }
          sx={{
            mt: 0.5,
            color: "#000",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            ...(long && { cursor: "zoom-in" }),
            ...(long &&
              !full && {
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              })
          }}
        >
          {reply.reply}
        </Typography>
      ) : null}
      {reply.dmReply ? (
        <Typography
          variant="body2"
          sx={{ mt: 0.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
        >
          <b>DM:</b> {reply.dmReply}
        </Typography>
      ) : null}
      {reply.notes ? (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.5,
            fontStyle: "italic",
            color: "text.secondary"
          }}
        >
          Not: {reply.notes}
        </Typography>
      ) : null}
    </Clickable>
  )
}

function Round({
  round,
  isLast,
  reply,
  replyMeta,
  onJump
}: {
  round: LoopRound
  isLast: boolean
  reply: LoopReply | null
  replyMeta: string[]
  onJump?: Jump
}) {
  const tokenHint = `${tokens(round.inputTokens)} okudu · ${tokens(round.outputTokens)} yazdı — modele giden metin ${round.inputTokens} token${
    round.cachedTokens
      ? ` (${round.cachedTokens} tanesi önceki çağrıdan önbellekte, daha ucuz)`
      : ""
  }; modelin ürettiği ${round.outputTokens} token.`
  const writesReply = isLast && round.calls.length === 0 && !!reply

  return (
    <Box sx={{ display: "flex", gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            bgcolor: round.failed ? FAILED_FG : "#5f43db",
            color: "common.white",
            fontSize: 11,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {round.index}
        </Box>
        {!isLast && (
          <Box sx={{ flex: 1, width: "1px", bgcolor: "divider", my: 0.25 }} />
        )}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1, pb: isLast ? 0 : 1 }}>
        <Clickable stepId={round.stepId} onJump={onJump}>
          <Tooltip
            arrow
            placement="top-start"
            title={round.inputTokens || round.outputTokens ? tokenHint : ""}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 0.75,
                px: 0.5,
                minHeight: 20
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: round.failed ? FAILED_FG : "text.secondary"
                }}
              >
                {round.failed
                  ? "Model çağrısı hata verdi"
                  : writesReply
                    ? "Cevabı yazdı"
                    : round.calls.length > 1
                      ? `${round.calls.length} araç çağırdı`
                      : round.calls.length === 1
                        ? "Araç çağırdı"
                        : "Araç çağırmadı"}
              </Typography>
              <Duration ms={round.durationMs} />
            </Box>
          </Tooltip>
        </Clickable>

        {round.error ? (
          <Typography
            variant="caption"
            sx={{ display: "block", px: 0.5, color: FAILED_FG }}
          >
            {round.error}
          </Typography>
        ) : null}
        {round.text ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              px: 0.5,
              fontStyle: "italic",
              color: "text.secondary",
              whiteSpace: "pre-wrap"
            }}
          >
            {round.text}
          </Typography>
        ) : null}

        {round.calls.map((call) => (
          <ToolCallRow key={call.stepId} call={call} onJump={onJump} />
        ))}

        {writesReply && reply ? (
          <ReplyBubble reply={reply} meta={replyMeta} onJump={onJump} />
        ) : null}
      </Box>
    </Box>
  )
}

/**
 * The agent loop read top to bottom — one line per host step, one block per model call with the
 * tools it chose, why, and what came back. Clicking any row opens that raw step below.
 */
export default function V2AgentLoop({
  loop,
  replyMeta = [],
  onJumpToStep
}: {
  loop: V2AgentLoopData
  /** Short facts about the decision (confidence, language…) shown on the reply's header line. */
  replyMeta?: string[]
  onJumpToStep?: Jump
}) {
  const lastRound = loop.rounds[loop.rounds.length - 1]
  const replyShownInRound =
    !!loop.reply && !!lastRound && lastRound.calls.length === 0
  const prepareFailed = loop.prepare.some((line) => line.failed)
  const [prepareOpen, setPrepareOpen] = useState(prepareFailed)

  return (
    <Box sx={{ minWidth: 0 }}>
      {loop.prepare.length > 0 && (
        <>
          <PhaseLabel
            label="Hazırlık"
            hint={PHASE_HINTS.prepare}
            ms={loop.prepareMs}
            open={prepareOpen}
            onToggle={() => setPrepareOpen((prev) => !prev)}
            collapsedText={loop.prepare.map((line) => line.label).join(" · ")}
          />
          {prepareOpen && (
            <HostLines lines={loop.prepare} onJump={onJumpToStep} />
          )}
        </>
      )}

      <PhaseLabel
        label={`Ajan · ${loop.rounds.length} model çağrısı`}
        hint={PHASE_HINTS.agent}
        ms={loop.agentMs}
      />
      {loop.rounds.map((round) => (
        <Round
          key={round.stepId}
          round={round}
          isLast={round === lastRound}
          reply={loop.reply}
          replyMeta={replyMeta}
          onJump={onJumpToStep}
        />
      ))}
      {loop.reply && !replyShownInRound ? (
        <ReplyBubble
          reply={loop.reply}
          meta={replyMeta}
          onJump={onJumpToStep}
        />
      ) : null}

      {loop.settle.length > 0 && (
        <>
          <PhaseLabel label="Teslim" hint={PHASE_HINTS.settle} />
          <HostLines lines={loop.settle} onJump={onJumpToStep} />
        </>
      )}
    </Box>
  )
}
